// src/components/shared/StudentNav.js
/* eslint-disable no-unused-vars */
//
// Shared nav bar for all student pages.
// Includes: motus logo, 5 tabs, upload button, notification bell with dropdown, avatar with dropdown.

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import MotusLogo from './MotusLogo';
import MotusButton from './MotusButton';

const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0',
  border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)',
  green:'#22C55E', greenDim:'rgba(34,197,94,0.08)',
  orange:'#F05A1F', orangeDim:'rgba(240,90,31,0.08)',
  purple:'#9333EA', red:'#DC2626',
  t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";

const TABS = [
  { key: 'dashboard', label: 'Home', path: '/me' },
  { key: 'videos', label: 'My Videos', path: '/me/videos' },
  { key: 'lessons', label: 'Lessons', path: '/me/lessons' },
  { key: 'progress', label: 'Progress', path: '/me/progress' },
  { key: 'messages', label: 'Messages', path: '/inbox?tab=messages' }, // Pass 75 — canonical Inbox
];

function StudentNav({ activeTab }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const userName = userData?.name || 'Student';
  const initials = userName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  // Pass 85.1B.1 (§6) — the notification bell/dropdown moved to the GlobalHeader; StudentNav no longer starts a
  // UID-global notifications listener (a hook whose data is never rendered is still a live Firestore read). The header
  // is the single, workspace-aware badge/entry point.

  // Detect active tab from path if not specified
  const currentTab = activeTab || detectTab(location.pathname);

  const handleLogout = async () => {
    try { await signOut(auth); navigate('/login'); } catch (_) {}
  };

  return (
    <nav style={{ height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '0 36px', borderBottom: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(18px)', position: 'sticky', top: 56, zIndex: 90 }}>

      {/* Workspace label (logo/notifications/account live in the GlobalHeader) */}
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.blue, whiteSpace: 'nowrap' }}>My Coaching</span>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, background: C.card, border: `1px solid ${C.border}`, borderRadius: 11, padding: 3 }}>
        {TABS.map(tab => {
          const isActive = currentTab === tab.key;
          return (
            <button key={tab.key} onClick={() => navigate(tab.path)}
              style={{ padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? '#FFF' : C.t3, border: 'none', background: isActive ? C.blue : 'none', fontFamily: font, cursor: 'pointer', transition: 'all .18s' }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Right actions — Upload only; notifications + account are in the GlobalHeader */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <MotusButton variant="primary-pill" size="sm" onClick={() => navigate('/me/upload')}>⬆ Upload Video</MotusButton>
      </div>
    </nav>
  );
}

function Dropdown({ children, wide }) {
  return (
    <div style={{ position: 'absolute', top: 42, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '6px 0', minWidth: wide ? 280 : 180, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 200, animation: 'fadeIn .15s ease' }}>
      {children}
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function DropItem({ icon, label, sub, color, bold, unread, onClick }) {
  const bgDefault = unread ? 'rgba(37,99,235,0.06)' : 'transparent';
  const bgHover = unread ? 'rgba(37,99,235,0.10)' : C.card2;
  return (
    <div onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: 14, fontWeight: bold ? 600 : 400, color: color || C.t1, cursor: 'pointer', fontFamily: font, transition: 'background .12s', background: bgDefault, borderLeft: unread ? '3px solid #2563EB' : '3px solid transparent' }}
      onMouseEnter={e => e.currentTarget.style.background = bgHover}
      onMouseLeave={e => e.currentTarget.style.background = bgDefault}>
      {icon && <span style={{ fontSize: 16, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: unread ? C.t1 : C.t2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: unread ? C.t2 : C.t3, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
      </div>
    </div>
  );
}

function NavIcon({ d, color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color || C.t2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {d.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
    </svg>
  );
}

function DotIcon({ color }) {
  return <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
}

function detectTab(pathname) {
  if (pathname.includes('/videos')) return 'videos';
  if (pathname.includes('/lessons')) return 'lessons';
  if (pathname.includes('/progress')) return 'progress';
  if (pathname.includes('/messages') || pathname.includes('/notifications') || pathname.includes('/inbox')) return 'messages';
  if (pathname.includes('/upload') || pathname.includes('/review')) return 'videos';
  if (pathname.includes('/my-bookings')) return 'dashboard';
  if (pathname.includes('/discover')) return 'dashboard';
  return 'dashboard';
}

export default StudentNav;
